import React, { useCallback, useMemo, useRef } from "react";
import { Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { Button, Chip, DateIntervalField, MenuItem, SelectField, Stack, TextField, Typography } from "@common/components/";
import { OverflowMenu } from "@icoach/components";
import { Box } from "@curves/ui-base";
import { Overview } from "@curves/ui-shared";
import AccountBookDialogBox from "@icoach/accountBook/dialog/AccountBookDialogBox";
import PageLayout from "@icoach/components/layout/PageLayout";
import { buildOrderMenuData } from "@icoach/accountBook/utils/buildOrderMenuData";
import { ACCOUNT_BOOK_ORDERS_ORDER_URL } from "@icoach/router/routerPath";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import useInvoiceApi from "@apis/useInvoiceApi";
import { ALLOWANCE_STATUS } from "@icoach/constants/dataEnums";
import { COLOR_MATCH } from "@icoach/accountBook/staticData";
import { callRef, ensureArray, formatCurrencyFn, refIsRequiredError } from "@util/utils";
import { getCurrentEndDay, getCurrentStartDate, getFirstOfMonth, getToday, isValidDate, parseDate } from "@util/moment";

const triplicateAllowanceOverviewHeaderRow = [
	{
		cellKey: "orderNo",
		headerLabel: "訂單",
		align: "center",
		width: "18",
		formatCell: (orderNo, row) => {
			const { orderID, invoiceProcessStatusText, triplicateAllowanceStatusText, invoiceProcessStatusColor, triplicateAllowanceStatusColor, statusText, statusColor } = row;
			const chipLabel = invoiceProcessStatusText ?? triplicateAllowanceStatusText ?? statusText ?? "—";
			const colorKey = invoiceProcessStatusColor ?? triplicateAllowanceStatusColor ?? statusColor ?? 3;
			return (
				<Stack direction={"column"}>
					<Link className={"mb-1"} to={ACCOUNT_BOOK_ORDERS_ORDER_URL(orderID)}>
						{orderNo}
					</Link>
					<Chip label={chipLabel} color={COLOR_MATCH[colorKey] ?? "grey"} title={"發票／折讓狀態"} />
				</Stack>
			);
		},
	},
	{
		cellKey: "receiverName",
		headerLabel: "購買人",
		width: "14",
		formatCell: (receiverName, { receiverTypeText }) => {
			return (
				<React.Fragment>
					<Typography variant="subtitle1" className="p-0 font-weight-bold">
						{receiverName ?? "—"}
					</Typography>
					<Typography variant="body2" className="mb-0 font-color-3">{`身份：${receiverTypeText ?? "—"}`}</Typography>
				</React.Fragment>
			);
		},
	},
	{
		cellKey: "orderTypeText",
		headerLabel: "訂單類型",
		width: "12",
		align: "center",
	},
	{
		cellKey: "triplicateInvoice",
		headerLabel: "三聯發票內容",
		width: "18",
		formatCell: (_, row) => (
			<Stack direction="column" alignItems="flex-start">
				<Typography variant="body2" className="mb-0">{`統一編號：${row.taxID ?? "—"}`}</Typography>
				<Typography variant="body2" className="mb-0">{`發票抬頭：${row.taxTitle ?? "—"}`}</Typography>
			</Stack>
		),
	},
	{
		cellKey: "payablePrice",
		headerLabel: "訂單金額",
		align: "right",
		width: "12",
		formatCell: cellValue => formatCurrencyFn(cellValue, { symbol: "$" }),
	},
	{
		cellKey: "orderDate",
		headerLabel: "建立日期",
		width: "14",
		formatCell: cellValue => {
			if (!isValidDate(cellValue)) return "—";
			const _parseDate = (date, format) => parseDate(date, format);
			return (
				<React.Fragment>
					<Typography variant={"body1"} className={"mb-0"}>
						{_parseDate(cellValue, "YYYY/MM/DD")}
					</Typography>
					<Typography variant={"body1"} className={"mb-0 font-color-3"}>
						{_parseDate(cellValue, "HH:mm")}
					</Typography>
				</React.Fragment>
			);
		},
	},
	{
		cellKey: "orderActions",
		align: "center",
		headerLabel: "",
		width: "7",
		// refresh／dialogRef 來自 Overview.Table 的 cellRenderContext（上傳成功後由 dialog 呼叫 refresh 重打列表）
		formatCell: (_, row, { refresh, dialogRef }) => {
			const menuData = buildOrderMenuData(row, { refresh, dialogRef });
			return menuData?.length ? <OverflowMenu menuData={menuData} /> : null;
		},
	},
];

/**
 * 營收管理 — 三聯發票折讓總覽（店端）
 * getParamsData：組 filters；getOverviewApi 內組 POST body
 */
const TriplicateInvoiceAllowanceOverviewPage = () => {
	const { getInvoiceAllowanceTriplicateUniformOverviewOptionsApi, postInvoiceAllowanceTriplicateUniformOverviewApi } = useInvoiceApi();
	const { options } = useOptions({
		getOptionsApi: getInvoiceAllowanceTriplicateUniformOverviewOptionsApi,
		isFetchOnMount: true,
	});
	const { allowanceStatusOptions = [] } = options || {};

	const defaultSearchParams = useMemo(
		() => {
			const today = getToday();
			return {
				pageIndex: 1,
				pageSize: 20,
				name: "",
				startDate: getCurrentStartDate(getFirstOfMonth(today, -3)),
				endDate: getCurrentEndDay(today),
				allowanceStatus: String(ALLOWANCE_STATUS.ALL),
			};
		},
		// eslint-disable-next-line
		[],
	);

	const { searchParams, overviewData, getOverviewData, updateOverviewPage } = useOverview({
		getOverviewApi: postInvoiceAllowanceTriplicateUniformOverviewApi,
		defaultSearchParams,
		enableQueryString: true,
		enableInfiniteScroll: false,
		isFetchOnMount: true,
	});

	const dialogRef = useRef(null);
	const refreshList = useCallback(() => getOverviewData(searchParams), [getOverviewData, searchParams]);
	const cellRenderContext = useMemo(() => ({ refresh: refreshList, dialogRef }), [refreshList, dialogRef]);

	const nameRef = useRef(null);
	const dateRangeRef = useRef(null);
	const allowanceStatusRef = useRef(null);

	// 組出給 useOverview 的 filters（以表單 ref 為準；非必填清空則送空字串，不沿用 searchParams 舊值）
	const getParamsData = () => {
		const nameVal = callRef(nameRef, "getResult");
		const dateRange = callRef(dateRangeRef, "getResult");
		const statusVal = callRef(allowanceStatusRef, "getResult");

		let startDate = "";
		let endDate = "";
		if (dateRange) {
			const { startDate: start, endDate: end } = dateRange;
			startDate = getCurrentStartDate(start);
			endDate = getCurrentEndDay(end);
		}

		return {
			pageIndex: searchParams?.pageIndex || 1,
			pageSize: searchParams?.pageSize || 20,
			name: nameVal != null ? String(nameVal).trim() : "",
			startDate,
			endDate,
			allowanceStatus: String(statusVal ?? ""),
		};
	};

	const handleSearchSubmit = () => {
		if (refIsRequiredError(allowanceStatusRef, nameRef, dateRangeRef)) return false;
		return getParamsData();
	};

	const overviewContextValue = useMemo(
		() => ({
			overviewData,
			searchParams,
			getOverviewData,
			updateOverviewPage,
		}),
		[overviewData, searchParams, getOverviewData, updateOverviewPage],
	);

	return (
		<PageLayout>
			<PageLayout.Header title="三聯發票折讓總覽" />
			<Overview contextValue={overviewContextValue} idKey="orderID" className="p-0">
				<Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
					<Grid spacing={2} container>
						<Grid xs={12} md={6} item>
							<TextField
								label="訂單/購買人"
								ref={nameRef}
								key={searchParams?.name}
								defaultValue={searchParams.name}
								InputProps={{ placeholder: "請輸入訂單編號或購買人或會員編號" }}
								fullWidth
							/>
						</Grid>
						<Grid xs={12} md={6} item>
							<SelectField label="折讓狀態" ref={allowanceStatusRef} key={searchParams?.allowanceStatus} defaultValue={searchParams?.allowanceStatus} fullWidth required>
								{ensureArray(allowanceStatusOptions).map(({ text, value, disabled }) => (
									<MenuItem key={value} value={value} disabled={disabled}>
										{text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
						<Grid xs={12} item>
							<DateIntervalField
								label="建立日期"
								ref={dateRangeRef}
								startDateProps={{
									key: searchParams?.startDate,
									defaultValue: searchParams?.startDate,
								}}
								endDateProps={{
									key: searchParams?.endDate,
									defaultValue: searchParams?.endDate,
								}}
								fullWidth
							/>
						</Grid>
					</Grid>
					<Box className="mt-3 text-right">
						<Button type="submit" variant="contained">
							搜尋
						</Button>
					</Box>
				</Overview.Search>

				<Stack direction="row" justifyContent="end" alignItems="center" spacing={2} className="mb-2">
					<Overview.TotalCount className="mb-0" showCurrentCount />
				</Stack>

				<Overview.Table className="mb-3" headerRow={triplicateAllowanceOverviewHeaderRow} cellRenderContext={cellRenderContext} />

				<Overview.Pagination />
			</Overview>
			<AccountBookDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default TriplicateInvoiceAllowanceOverviewPage;
